import { Stack } from '@mui/material'
import { BarChart, BarChartProps } from '@mui/x-charts/BarChart'
import { format, parseISO } from 'date-fns'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ContentBarSvg } from '../Icons/ContentBarSvg'
import { MUIMultiselectAsset } from '../Shared'

const translations = {
  SUCCESS: 'SUCCESS',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  FAILURE: 'FAILURE',
  IN_QUEUE: 'IN_QUEUE',
  // PUBLISHED: 'PUBLISHED',
  // DEPUBLISHED: 'DEPUBLISHED',
  INITIATED: 'INITIATED',
  // CREATED: 'CREATED',
} as const

const jobCategoryData = [
  { color: '#1BBA70', value: 'SUCCESS', key: 'Success' },
  { color: '#957EF9', value: 'INITIATED', key: 'Initiated' },
  { color: '#FFB300', value: 'IN_QUEUE', key: 'In Queue' },
  { color: '#E36E3B', value: 'FAILURE', key: 'Failure' },
  { color: '#D4351C', value: 'VALIDATION_FAILED', key: 'Validation Failed' },
]

const addLabels = <T extends { dataKey: keyof typeof translations }>(series: T[]) => {
  return series.map((item) => ({
    ...item,
    valueFormatter: (v: number | null) => `${v}`,
  }))
}

const filterJobStatusData = (data: any, selectedCategories?: any, dateRangeFilter?: string) => {
  if (!data?.jobstatus || !Array.isArray(data.jobstatus)) return []

  let filtered = data.jobstatus

  // Date range filtering
  if (dateRangeFilter) {
    const today = new Date()
    const daysAgo = dateRangeFilter === 'last30days' ? 30 : 7
    const startDate = new Date(today)
    startDate.setHours(0, 0, 0, 0)
    today.setHours(23, 59, 59, 999)
    startDate.setDate(today.getDate() - daysAgo + 1)

    filtered = filtered.filter((item: any) => {
      if (!item.day) return false
      try {
        const itemDate = new Date(item.day)
        itemDate.setHours(0, 0, 0, 0)
        return itemDate >= startDate && itemDate <= today
      } catch {
        return false
      }
    })
  }

  // If categories are selected, filter out days with no selected category values
  if (selectedCategories?.length > 0) {
    filtered = filtered.map((item: any) => {
      const filteredCounts: Record<string, number> = {}
      selectedCategories.forEach((cat: any) => {
        filteredCounts[cat?.value] = item.counts[cat?.value] ?? 0
      })
      return { ...item, counts: filteredCounts }
    })
  }

  return filtered.map((item: any) => {
    const counts: Record<string, number> = {}
    Object.keys(translations).forEach((key) => {
      counts[key] = item?.counts?.[key] ?? 0
    })
    return {
      ...counts,
      day: item?.day,
    }
  })
}

const customTooltip = ({ itemData, series }: any) => {
  const { label, data } = series
  const count = data[itemData?.dataIndex]
  if (label) {
    return (
      <Stack
        className='graph-tooltip'
        sx={{
          background: '#2E353B',
          font: 'Lato',
          fontWeight: 500,
          fontSize: '13px',
          lineHeight: '15.6px',
          color: '#fff',
          opacity: '90%',
          borderRadius: '10px',
        }}
        marginLeft={3}
        marginTop={-5}
        direction='column'
        gap={2}
        padding={2}
        minWidth={'100px'}
        minHeight={'56px'}
      >
        <Stack className='graph-tooltip-label'>{label}</Stack>
        <Stack className='graph-tooltip-value'>{count}</Stack>
      </Stack>
    )
  }
  return null
}

export interface ContentBarChartProps {
  jobstatus: Array<{
    day: string
    counts: Record<string, number>
  }>
}

const ContentBarChart = ({ jobstatus }: ContentBarChartProps) => {
  const navigate = useNavigate()
  const [selectedFilter, setSelectedFilter] = useState({ newValue: [] as any[] })
  const [dateRangeFilter, setDateRangeFilter] = useState<string>('last7days')
  const [jobStatusData, setJobStatusData] = useState<Array<any>>([])

  useEffect(() => {
    setJobStatusData(
      filterJobStatusData({ jobstatus }, selectedFilter?.newValue, dateRangeFilter) || [],
    )
  }, [selectedFilter, dateRangeFilter, jobstatus])

  const handleDateRangeChange = (newValue: any) => {
    setDateRangeFilter(newValue?.value || 'last7days')
  }

  const handleCategoryChange = (newValue: any) => {
    setSelectedFilter((prev) => ({ ...prev, newValue }))
  }

  return (
    <div className='content-category-wrapper'>
      <div className='d-flex justify-content-between position-relative'>
        <h4 className='d-flex gap-6'>
          <ContentBarSvg />
          Job State Achievement
        </h4>
        <div className='d-flex' style={{ gap: 16 }}>
          <div className='category-dropdown-bar home-category-dropdown d-flex'>
            <MUIMultiselectAsset
              name='day'
              multiple
              options={jobCategoryData}
              onInputChange={(_ev: any, newValue: any) => handleCategoryChange(newValue)}
              id='day'
              value={selectedFilter?.newValue}
              label=''
              labelKey='key'
              valueKey='value'
              small
              chipProps={{ sx: { backgroundColor: 'transparent', padding: '0px' } }}
            />
            <MUIMultiselectAsset
              name='dateRange'
              multiple={false}
              options={[
                { value: 'last7days', key: 'Last 7 Days' },
                { value: 'last30days', key: 'Last 30 Days' },
              ]}
              onInputChange={(_ev: any, newValue: any) => handleDateRangeChange(newValue)}
              id='dateRange'
              value={{
                value: dateRangeFilter,
                key: dateRangeFilter === 'last7days' ? 'Last 7 Days' : 'Last 30 Days',
              }}
              label=''
              labelKey='key'
              valueKey='value'
              small
              chipProps={{ sx: { backgroundColor: 'transparent', padding: '0px' } }}
            />
          </div>
        </div>
      </div>
      <BarChart
        onItemClick={() => {
          navigate('/reports/content-ingestion-report', {
            state: {
              isFromBarChart: true,
              selectedFilter: selectedFilter,
              dateRangeFilter: dateRangeFilter,
            },
          })
        }}
        dataset={
          jobStatusData.length > 0
            ? jobStatusData
            : [{ day: '', ...Object.fromEntries(Object.keys(translations).map((key) => [key, 0])) }]
        }
        series={addLabels(
          jobCategoryData.map((item) => ({
            label: item.key,
            dataKey: item.value as keyof typeof translations,
            stack: 'assets',
            color: item.color,
          })),
        )}
        xAxis={[
          {
            dataKey: 'day',
            scaleType: 'band',
            tickSize: 0,
            disableTicks: false, // Show ticks for all bars
            valueFormatter: (value: string) => {
              if (!value) return ''
              try {
                return format(parseISO(value), 'dd MMM')
              } catch {
                return value
              }
            },
            label: '',
            tickLabelStyle: {
              ...(dateRangeFilter === 'last30days'
                ? { angle: -45, textAnchor: 'end', fontSize: 12, fontWeight: 400 }
                : { angle: 0, textAnchor: 'middle', fontSize: 12 }),
              // keep text white for contrast
              paintOrder: 'stroke fill',
              color: '#615E83',
              fontWeight: 400, // not standard SVG, so use sx below
            },
          },
        ]}
        sx={{
          '& .MuiBarElement-root': {
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          },
          '& .MuiChartsAxis-root.MuiChartsAxis-directionY.MuiChartsAxis-left .MuiChartsAxis-line': {
            display: 'none', // Hides the y-axis line
          },
          '& .MuiChartsAxis-tickLabel': {
            background: '#615E83',
            borderRadius: 4,
            padding: '2px 6px',
            fill: '#fff',
            fontWeight: 500,
          },
          '& .MuiChartsAxis-tick[data-axis="y"] .MuiChartsAxis-tickLabel': {
            background: 'none',
            fill: '#7B91B0',
            color: '#7B91B0',
            fontWeight: 500,
            padding: 0,
          },
          '& .MuiChartsAxis-tick': { display: 'none' },
          '& .MuiChartsGrid-line[transform*="translateY"]': {
            display: 'block',
            stroke: '#EFF1F3',
            strokeWidth: 2,
          },
          '& .MuiChartsGrid-line[transform*="translateX"]': {
            display: 'block',
            stroke: '#EFF1F3',
            strokeWidth: 2,
          },
        }}
        yAxis={[{ label: '' }]}
        slotProps={{ legend: { hidden: true } }}
        tooltip={{
          itemContent: customTooltip,
          trigger: 'item',
        }}
        {...config}
      />
      <ul className='category-list-bar' style={{ marginTop: '30px' }}>
        {jobCategoryData.map((item) => (
          <li key={item.value}>
            <span style={{ backgroundColor: item.color }}></span>
            <span> {item.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const config: Partial<BarChartProps> = {
  height: 400,
}

export default ContentBarChart
